/**
 * Persistence types for the sandbox backend
 * 
 * The URL scheme follows JSFiddle's pattern: /<hash>/<revision>
 * - hash: A unique identifier for the sandbox (derived from content hash or random)
 * - revision: An incremental version number starting at 1
 */

import type { GboxDocument } from './gbox';

/**
 * A persisted sandbox entry in the database
 */
export interface PersistedSandbox {
  /** Unique hash identifier for this sandbox lineage */
  hash: string;
  /** Revision number within this sandbox lineage (1-indexed) */
  revision: number;
  /** The full sandbox data (compressed) */
  data: GboxDocument;
  /** When this revision was created */
  createdAt: string;
  /** When this revision was last accessed */
  lastAccessedAt: string;
  /** Whether this is a fork of another sandbox */
  forkedFrom?: {
    hash: string;
    revision: number;
  };
  /** Access statistics */
  stats: {
    views: number;
    forks: number;
  };
}

/**
 * Request to save a new sandbox or create a new revision
 */
export interface SaveSandboxRequest {
  /** If provided, creates a new revision of an existing sandbox */
  parentHash?: string;
  /** If provided with parentHash, creates a fork */
  fork?: boolean;
  /** The sandbox data to save (object or compressed base64 string) */
  data: GboxDocument | string;
  /** Optimistic-concurrency guard: the head revision the client based this append on.
   *  Engine-api appends expectedHeadRevision+1 and returns 409 REVISION_CONFLICT on a
   *  stale head. Absent = deprecated append-at-head (legacy backend ignores it). */
  expectedHeadRevision?: number;
}

/**
 * Response from saving a sandbox
 */
export interface SaveSandboxResponse {
  /** The hash of the saved sandbox */
  hash: string;
  /** The revision number */
  revision: number;
  /** Full URL path to access this sandbox */
  url: string;
  /** Present ONLY on create/fork responses from engine-api: the one-time edit-capability
   *  token for the NEW lineage. The client stores it (localStorage) keyed by hash. */
  editToken?: string;
}

/**
 * Request to load a sandbox
 */
export interface LoadSandboxRequest {
  /** The sandbox hash */
  hash: string;
  /** Optional revision number (defaults to latest) */
  revision?: number;
}

/**
 * Response from loading a sandbox
 */
export interface LoadSandboxResponse {
  /** The loaded sandbox */
  sandbox: PersistedSandbox;
  /** All available revisions for this hash */
  revisions: {
    revision: number;
    createdAt: string;
    name: string;
  }[];
  /** The latest revision number */
  latestRevision: number;
}

/**
 * Sandbox listing for browsing
 */
export interface SandboxListItem {
  hash: string;
  latestRevision: number;
  name: string;
  description?: string;
  author?: string;
  tags?: string[];
  createdAt: string;
  updatedAt: string;
  stats: {
    views: number;
    forks: number;
  };
}

/**
 * Search/filter options for listing sandboxes
 */
export interface SandboxListOptions {
  /** Search query (matches name, description, tags) */
  query?: string;
  /** Filter by tags */
  tags?: string[];
  /** Filter by author */
  author?: string;
  /** Sort field */
  sortBy?: 'createdAt' | 'updatedAt' | 'views' | 'forks' | 'name';
  /** Sort direction */
  sortOrder?: 'asc' | 'desc';
  /** Pagination offset */
  offset?: number;
  /** Number of results per page */
  limit?: number;
}

/**
 * Response from listing sandboxes
 */
export interface SandboxListResponse {
  items: SandboxListItem[];
  total: number;
  offset: number;
  limit: number;
}

/**
 * Generate a short hash from content
 * Uses a simple hash that produces a short, URL-safe string
 */
export function generateShortHash(content: string): string {
  // Simple FNV-1a hash converted to base36 for short URLs
  let hash = 2166136261;
  for (let i = 0; i < content.length; i++) {
    hash ^= content.charCodeAt(i);
    hash = (hash * 16777619) >>> 0;
  }
  // Add timestamp component for uniqueness
  const timestamp = Date.now().toString(36);
  const hashPart = hash.toString(36);
  return `${hashPart}${timestamp.slice(-4)}`;
}

/**
 * Validate a sandbox hash format
 */
export function isValidHash(hash: string): boolean {
  // Hash should be 6-12 alphanumeric characters
  return /^[a-z0-9]{6,12}$/.test(hash);
}

/**
 * Parse a sandbox URL path into hash and revision
 */
export function parseSandboxPath(path: string): { hash: string; revision?: number } | null {
  const match = path.match(/^\/([a-z0-9]{6,12})(?:\/(\d+))?$/);
  if (!match) return null;
  return {
    hash: match[1],
    revision: match[2] ? parseInt(match[2], 10) : undefined,
  };
}

/**
 * Build a sandbox URL path from hash and revision
 */
export function buildSandboxPath(hash: string, revision?: number): string {
  if (revision !== undefined) {
    return `/${hash}/${revision}`;
  }
  return `/${hash}`;
}
